import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { catchError, retry } from 'rxjs/operators';
import { AlertService, IAlertType } from "./alert.service";
import {
  CrmSyncRequest,
  PersonCrmCompareResponse,
  WebserviceResponse,
  Personen
} from "../interfaces";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class PersonsStoreService {
  private readonly endpoint = restBaseUrl();

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) {
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  getAllPersonen() {
    const options = new HttpParams({
      fromObject: {
        person_id: '0',
        contact_id: '0',
        active: true
      }
    });
    return this.http.get<WebserviceResponse<Personen[]>>(this.endpoint + '/persons/get_persons',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getSinglePersons(personid: number) {
    const options = new HttpParams({
      fromObject: {
        person_id: personid
      }
    });
    return this.http.get<WebserviceResponse<Personen[]>>(this.endpoint + '/persons/get_single_person',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getPersonInCompany(companyid: number, active?: boolean) {
    let options = new HttpParams()
      .set('person_id', '0')
      .set('contact_id', String(companyid));

    if (active !== undefined) {
      options = options.set('active', String(active));
    }

    return this.http.get<WebserviceResponse<Personen[]>>(this.endpoint + '/persons/get_persons',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  createPerson(person: Personen, companyID: number) {
    return this.http.post<{ object: boolean; hasErrors: boolean; message: string; errors: any[] }>(
        `${this.endpoint}/persons/add_person?contact_id=${companyID}`,
        person,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  updatePerson(person: Personen): Observable<any> {
    return this.http.put(
        `${this.endpoint}/persons/update_person?person_id=${person.pid}`,
        person,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  deletePerson(personID: number): Observable<any> {
    return this.http.delete(
        `${this.endpoint}/persons/delete_person?person_id=${personID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getCrmCompare(personId: number, sessionId: string): Observable<PersonCrmCompareResponse> {
    let params = new HttpParams();
    if (sessionId) {
      params = params.set('session_id', sessionId);
    }

    return this.http.get<PersonCrmCompareResponse>(
      `${this.endpoint}/persons/${personId}/crm_compare`,
      { params }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  syncCrmPerson(personId: number, payload: CrmSyncRequest): Observable<PersonCrmCompareResponse> {
    return this.http.post<PersonCrmCompareResponse>(
      `${this.endpoint}/persons/${personId}/crm_sync`,
      payload,
      { responseType: 'json' }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }
}
